import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { MoneyTextInput } from '@/components/ui/money-input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { Product } from '@/types/database';
import { ArrowLeft, Package, Plus, Loader2, Search, Trash2, Copy, Camera, X, Boxes, ScanLine, ReceiptText, Layers, WifiOff, Calculator, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useCamera } from '@/hooks/useCamera';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { searchServicos, getCorrelacaoByLC116 } from '@/lib/fiscalData';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useFeature } from '@/hooks/useFeature';
import { StorageImage } from '@/components/ui/StorageImage';
import { useHaptics } from '@/hooks/useHaptics';
import { cacheCatalog, getCachedCatalog } from '@/lib/offlineDataCache';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRole } from '@/hooks/useRole';
import { addToOfflineQueue, removeFromOfflineQueue } from '@/lib/offlineStorage';
import { formatStockQuantity } from '@/lib/stock';
import {
  groupVariants,
  isVariant,
  parentIdsWithVariants,
  parentPriceFromDrafts,
  parseVariantPrice,
  validateVariantDrafts,
  withVariantIds,
  type VariantDraft,
} from '@/lib/variants';
import { deleteProductsWithVariants } from '@/lib/productMutations';
import { formatCostInput, INVALID_COST_MESSAGE, parseCostInput, productUnitCost } from '@/lib/cost';
import {
  deletePendingUploads,
  trackPendingUpload,
  type PendingUpload,
} from '@/lib/pendingUploads';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';
import { normalizeUnit, unitGroupsIncluding, unitLabel } from '@/lib/units';
import {
  suggestInternalCode,
  isValidInternalCode,
  normalizeInternalCode,
  usedInternalCodes,
  INTERNAL_CODE_MAX_LENGTH,
} from '@/lib/productCodes';
import { MarkupCalculatorSheet } from '@/components/catalog/MarkupCalculatorSheet';
import { BundleMaterialsSection } from '@/components/catalog/BundleMaterialsSection';
import { VariantDraftsSection } from '@/components/catalog/VariantDraftsSection';
import { bundleQuantity, groupBundleItems, type BundleItemRow } from '@/lib/productBundles';
import { cacheBundles, getCachedBundles } from '@/lib/offlineDataCache';
import {
  PRODUCT_STOCK_MODE_INHERIT,
  PRODUCT_STOCK_MODE_OPTIONS,
  productStockModeFromSelect,
  type ProductStockDeductionMode,
} from '@/lib/stockSettings';
import {
  PRODUCT_PHOTO_LIMIT,
  moveProductPhotoToPrimary,
  normalizeProductPhotos,
  primaryProductPhotoUrl,
  productPhotoCountLabel,
  type ProductPhotoDraft,
} from '@/lib/productPhotos';

type ItemType = 'service' | 'product';

interface CatalogItem extends Product {
  type: ItemType;
  unit: string | null;
  category: string | null;
  product_photos?: ProductPhotoDraft[];
  _offline_photos?: ProductPhotoDraft[];
}

export default function CatalogItemFormPage() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const quotePhotos = useFeature('quote_photos');
  const inventory = useFeature('inventory');
  const productVariants = useFeature('product_variants');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isEdit = Boolean(id);
  const { isOnline, checkOnline, saveCatalogItemOffline } = useOfflineSync();
  const { canWrite, canDelete } = useRole();
  const { successFeedback } = useHaptics();

  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') as ItemType | null;

  const navState = (location.state as { initialType?: ItemType; initialBarcode?: string } | null) ?? null;

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [bundleRows, setBundleRows] = useState<BundleItemRow[]>([]);
  const [loadingItem, setLoadingItem] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [activeTab, setActiveTab] = useState<ItemType>(typeParam || navState?.initialType || 'service');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [unit, setUnit] = useState('');
  const [barcode, setBarcode] = useState(navState?.initialBarcode ?? '');
  const [internalCode, setInternalCode] = useState('');
  const [category, setCategory] = useState('');
  const [trackStock, setTrackStock] = useState(false);
  const [stockDeductionMode, setStockDeductionMode] =
    useState<ProductStockDeductionMode>(null);
  const [minStock, setMinStock] = useState('');
  const [variantDrafts, setVariantDrafts] = useState<VariantDraft[]>([]);
  const [lc116Item, setLc116Item] = useState('');
  const [issAliquota, setIssAliquota] = useState('');
  const [issRetido, setIssRetido] = useState(false);
  const [nbs, setNbs] = useState('');
  const [cclasstrib, setCclasstrib] = useState('');
  const [servicoQuery, setServicoQuery] = useState('');
  const [productPhotos, setProductPhotos] = useState<ProductPhotoDraft[]>([]);
  const imageUrl = primaryProductPhotoUrl(productPhotos);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [bundleDraft, setBundleDraft] = useState<BundleItemRow[]>([]);

  const { capturePhoto, isCapturing } = useCamera();
  const { uploadPhoto, isUploading: isUploadingPhoto } = usePhotoUpload();
  const { scan: scanBarcode, isAvailable: barcodeAvailable, isScanning: isBarcodeScanning } = useBarcodeScanner();
  const showBarcodeButton = barcodeAvailable || import.meta.env.DEV;

  const getEffectiveUserId = useCallback((): string | null => {
    if (user?.id) return user.id;
    try {
      const cachedSession = localStorage.getItem('orcamigo_cache_auth_session');
      if (cachedSession) {
        const sessionData = JSON.parse(cachedSession);
        if (sessionData?.data?.userId) return sessionData.data.userId;
      }
    } catch (e) {
      console.error('[CatalogItemFormPage] Error reading cached session:', e);
    }
    return null;
  }, [user]);

  // Load the whole catalog (needed for unique-code validation, variant lookup,
  // bundle material picker) — same source the list page uses.
  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) return;
    const cached = getCachedCatalog(userId);
    if (cached) {
      const mapped: CatalogItem[] = cached.map((item: any) => ({
        ...item,
        type: item.type || 'service',
        unit: item.unit || null,
      }));
      setItems(mapped);
    }
  }, [getEffectiveUserId]);

  useEffect(() => {
    if (!user || !isOnline || !activeCompany) return;
    (async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, product_photos(*)')
        .eq('company_id', activeCompany.id)
        .order('name');
      if (data && !error) {
        const mapped: CatalogItem[] = data.map((item: any) => ({
          ...item,
          type: item.type || 'service',
          unit: item.unit || null,
        }));
        setItems(mapped);
        cacheCatalog(user.id, data);
      }
    })();
  }, [user, isOnline, activeCompany?.id]);

  const fetchBundles = useCallback(async () => {
    const userId = getEffectiveUserId();
    if (!userId) return;
    const cached = getCachedBundles(userId);
    if (cached) setBundleRows(cached as BundleItemRow[]);
    if (!isOnline || !activeCompany) return;
    const { data, error } = await supabase
      .from('product_bundle_items')
      .select('*')
      .eq('company_id', activeCompany.id);
    if (!error && data) {
      setBundleRows(data as BundleItemRow[]);
      cacheBundles(userId, data);
    }
  }, [getEffectiveUserId, isOnline, activeCompany?.id]);

  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);

  const variantsByParent = useMemo(() => groupVariants(items), [items]);
  const bundlesByParent = useMemo(() => groupBundleItems(bundleRows), [bundleRows]);
  const parentsWithVariants = useMemo(() => parentIdsWithVariants(items), [items]);
  const kitParentIds = useMemo(
    () => new Set(bundleRows.map((r) => String(r.parent_product_id))),
    [bundleRows],
  );
  const materialIds = useMemo(
    () => new Set(bundleRows.map((r) => String(r.child_product_id))),
    [bundleRows],
  );
  const kitUsageByChild = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of bundleRows) {
      const key = String(row.child_product_id);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [bundleRows]);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .filter((i) => i.type === activeTab && i.category?.trim())
            .map((i) => i.category!.trim()),
        ),
      ).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items, activeTab],
  );

  const servicoResults = useMemo(
    () => (servicoQuery.trim() ? searchServicos(servicoQuery).slice(0, 8) : []),
    [servicoQuery],
  );

  /**
   * O código sugerido sai do NOME (`Camiseta Polo Azul` → `CAM-POL-AZU`), não de
   * caracteres aleatórios: ele existe para ser lido numa etiqueta e digitado de
   * volta na busca, e um `K7P2R9` não diz de que produto se trata. A unicidade
   * continua garantida — colisão ganha sufixo (`-2`, `-3`).
   */
  const generateUniqueCode = useCallback(
    (sourceName: string, extra: Array<string | null | undefined> = []) => {
      const used = usedInternalCodes(items, [
        internalCode,
        ...variantDrafts.map((d) => d.internal_code),
        ...extra,
      ]);
      const code = suggestInternalCode(sourceName, used);
      if (!code) {
        toast.error('Não foi possível gerar um código livre. Tente novamente.');
        return null;
      }
      return code;
    },
    [items, internalCode, variantDrafts],
  );

  const handleGenerateInternalCode = useCallback(() => {
    const code = generateUniqueCode(name);
    if (code) setInternalCode(code);
  }, [generateUniqueCode, name]);

  const handleScanBarcodeField = useCallback(async () => {
    const code = await scanBarcode();
    if (code) setBarcode(code.trim());
  }, [scanBarcode]);

  /** Carrega o formulário com um produto RAIZ (o pai, quando há variações). */
  const applyParent = (item: CatalogItem) => {
    setEditingItem(item);
    setName(item.name);
    setDescription(item.description || '');
    // O campo é MASCARADO: hidratar com outro formato corromperia o valor na
    // primeira edição (#153a). `numberToMaskedBRL` é o par de `maskBRLInput`.
    setPrice(numberToMaskedBRL(item.price));
    setCost(formatCostInput(item.cost));
    setUnit(normalizeUnit(item.unit) || '');
    setBarcode(item.barcode || '');
    setInternalCode(item.internal_code || '');
    setCategory(item.category || '');
    setTrackStock(item.track_stock === true);
    setStockDeductionMode(item.stock_deduction_mode ?? null);
    setMinStock(item.min_stock != null ? String(item.min_stock).replace('.', ',') : '');
    setLc116Item(item.lc116_item || '');
    setIssAliquota(item.iss_aliquota != null ? String(item.iss_aliquota).replace('.', ',') : '');
    setIssRetido(item.iss_retido === true);
    setNbs(item.nbs || '');
    setCclasstrib(item.cclasstrib || '');
    setServicoQuery('');
    setProductPhotos(normalizeProductPhotos(
      item.product_photos ?? item._offline_photos,
      item.image_url,
    ));
    setActiveTab(item.type);
  };

  const applyItem = (item: CatalogItem, allItems: CatalogItem[], childrenMap: Map<string, CatalogItem[]>) => {
    const target = isVariant(item)
      ? allItems.find((i) => i.id === item.parent_product_id) ?? item
      : item;

    const children = (childrenMap.get(String(target.id)) ?? []) as CatalogItem[];
    setVariantDrafts(
      children.map((child) => ({
        key: String(child.id),
        id: String(child.id),
        variant_name: child.variant_name ?? '',
        price: numberToMaskedBRL(child.price),
        cost: formatCostInput(child.cost),
        barcode: child.barcode ?? '',
        internal_code: child.internal_code ?? '',
        stock_quantity: Number(child.stock_quantity ?? 0),
      })),
    );

    applyParent(target);
    setBundleDraft((groupBundleItems(bundleRows).get(String(target.id)) ?? []) as BundleItemRow[]);

    if (children.length > 0) {
      const tracked = children.find((c) => c.track_stock === true);
      setTrackStock(Boolean(tracked));
      setMinStock(tracked?.min_stock != null ? String(tracked.min_stock).replace('.', ',') : '');
      const childCosts = children
        .map((c) => productUnitCost(c))
        .filter((n): n is number => n != null);
      setCost(childCosts.length > 0 ? formatCostInput(Math.min(...childCosts)) : '');
    }
    setLoadingItem(false);
  };

  // Load item for edit: prefer cache, then server (once `items` is loaded).
  useEffect(() => {
    if (!id || !user) return;
    if (items.length === 0) return;
    const match = items.find((i) => String(i.id) === id);
    if (!match) return;
    applyItem(match, items, variantsByParent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, items, variantsByParent]);

  useEffect(() => {
    if (id && items.length === 0 && !isOnline) {
      setLoadingItem(false);
    }
  }, [id, items.length, isOnline]);

  const handleCapturePhoto = async () => {
    if (!quotePhotos.require()) return;
    if (productPhotos.length >= PRODUCT_PHOTO_LIMIT) {
      toast.warning(`Limite de ${PRODUCT_PHOTO_LIMIT} fotos por item.`);
      return;
    }
    const result = await capturePhoto({ quality: 85, source: 'prompt' });
    if (result?.dataUrl) {
      const online = await checkOnline();
      if (online) {
        const uploadResult = await uploadPhoto(
          result.dataUrl,
          'product-photos',
          'catalog',
          activeCompany?.id,
        );
        if (uploadResult?.url) {
          // Rastreia para o descarte: se o cadastro for abandonado, é esta
          // lista que diz o que pode ser apagado sem tocar em foto alheia.
          pendingUploadsRef.current = trackPendingUpload(
            pendingUploadsRef.current,
            uploadResult.url,
          );
          setProductPhotos((current) => normalizeProductPhotos([
            ...current,
            { image_url: uploadResult.url, position: current.length },
          ]));
          toast.success('Foto adicionada!');
        }
      } else {
        setProductPhotos((current) => normalizeProductPhotos([
          ...current,
          { image_url: result.dataUrl, position: current.length },
        ]));
        toast.success('Foto adicionada! Será enviada quando houver internet.');
      }
    }
  };

  /**
   * ─── FOTO DE CATÁLOGO TAMBÉM SOBE NO ATO DA SELEÇÃO ───────────────────────
   *
   * Mesmo problema do formulário de orçamento e, aqui, sem rascunho autossalvo:
   * quem abandona o cadastro deixa o arquivo no bucket sem nenhuma linha
   * apontando para ele. Como não há rascunho a preservar, o gatilho pode ser o
   * DESMONTE da tela — com uma trava para o caso de sucesso, senão salvar o
   * item apagaria as fotos que ele acabou de ganhar.
   *
   * A lista guarda só o que ESTA tela subiu: as fotos que já existiam no
   * produto (edição) nunca entram, e portanto nunca são apagadas por descarte.
   */
  const pendingUploadsRef = useRef<PendingUpload[]>([]);
  const savedRef = useRef(false);

  useEffect(
    () => () => {
      if (savedRef.current) return;
      void deletePendingUploads(pendingUploadsRef.current);
    },
    [],
  );

  const handleRemovePhoto = (index: number) => {
    const alvo = productPhotos[index]?.image_url;
    // Removeu antes de salvar: se fomos nós que subimos, o arquivo já é órfão.
    const nossa = pendingUploadsRef.current.filter((entry) => entry.url === alvo);
    if (nossa.length > 0) {
      void deletePendingUploads(nossa);
      pendingUploadsRef.current = pendingUploadsRef.current.filter((entry) => entry.url !== alvo);
    }
    setProductPhotos((current) =>
      normalizeProductPhotos(current.filter((_, photoIndex) => photoIndex !== index)));
  };

  const persistProductPhotos = async (productId: string): Promise<string | null> => {
    if (!activeCompany?.id || !user?.id) return 'Selecione uma empresa ativa.';
    const photos = normalizeProductPhotos(productPhotos).map((photo, position) => ({
      image_url: photo.image_url,
      title: photo.title ?? null,
      description: photo.description ?? null,
      position,
    }));
    // A partir daqui as fotos TÊM dono: o desmonte da tela não pode mais
    // apagá-las. Marcado ANTES de checar o erro de propósito — se a RPC falhar
    // pela metade, o arquivo órfão é achável por varredura, enquanto apagar uma
    // foto que o banco já gravou não tem volta.
    savedRef.current = true;
    const { error } = await supabase.rpc('replace_product_photos', {
      p_product_id: productId,
      p_photos: photos,
    });
    return error ? 'Não foi possível salvar todas as fotos do produto.' : null;
  };

  const sameBundle = (a: BundleItemRow[], b: BundleItemRow[]) => {
    if (a.length !== b.length) return false;
    const key = (r: BundleItemRow) => `${r.child_product_id}:${bundleQuantity(r.quantity)}`;
    const setA = new Set(a.map(key));
    return b.every((r) => setA.has(key(r)));
  };

  const persistBundleItems = async (parentId: string): Promise<string | null> => {
    if (!activeCompany?.id) return 'Selecione uma empresa ativa para incluir materiais.';

    const previous = (bundlesByParent.get(String(parentId)) ?? []) as BundleItemRow[];
    const draftByChild = new Map(bundleDraft.map((r) => [String(r.child_product_id), r]));
    const previousByChild = new Map(previous.map((r) => [String(r.child_product_id), r]));

    const removed = previous.filter((r) => !draftByChild.has(String(r.child_product_id)));
    if (removed.length > 0) {
      const { error } = await supabase
        .from('product_bundle_items')
        .delete()
        .in('id', removed.map((r) => String(r.id)));
      if (error) return 'Não foi possível remover um material do serviço.';
    }

    for (const row of bundleDraft) {
      const existing = previousByChild.get(String(row.child_product_id));
      if (existing) {
        if (bundleQuantity(existing.quantity) === bundleQuantity(row.quantity)) continue;
        const { error } = await supabase
          .from('product_bundle_items')
          .update({ quantity: bundleQuantity(row.quantity) })
          .eq('id', String(existing.id));
        if (error) return 'Não foi possível atualizar a quantidade de um material.';
        continue;
      }

      const { error } = await supabase.from('product_bundle_items').insert({
        company_id: activeCompany.id,
        parent_product_id: String(parentId),
        child_product_id: String(row.child_product_id),
        quantity: bundleQuantity(row.quantity),
      });
      if (error) return (error as { message?: string })?.message || 'Não foi possível incluir um material.';
    }

    return null;
  };

  const handleSave = async () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    if (!name.trim()) {
      toast.warning('Nome é obrigatório');
      return;
    }

    const hasVariants = variantDrafts.length > 0;

    if (hasVariants) {
      if (activeTab !== 'product') {
        toast.warning('Variações só existem em produtos físicos.');
        return;
      }
      const check = validateVariantDrafts(variantDrafts);
      if (!check.ok) {
        toast.warning(check.error!);
        return;
      }
    }

    const priceValue = hasVariants
      ? parentPriceFromDrafts(variantDrafts, 0)
      // O texto vem da máscara, então é sempre pt-BR bem formado. `parseFloat` na
      // mão lia "1.500" como 1,5 — mil vezes menos, sem erro na tela (§3.17).
      : parseBRLInput(price);
    if (!hasVariants && (isNaN(priceValue) || priceValue <= 0)) {
      toast.warning('Informe um preço válido');
      return;
    }

    if (!unit) {
      toast.warning('Selecione a unidade de medida');
      return;
    }

    const parsedCost = parseCostInput(cost);
    if (!parsedCost.ok) {
      toast.warning(INVALID_COST_MESSAGE);
      return;
    }
    const costCols = { cost: hasVariants ? null : parsedCost.value };

    if (!isValidInternalCode(internalCode)) {
      toast.warning(`O código interno pode ter no máximo ${INTERNAL_CODE_MAX_LENGTH} caracteres.`);
      return;
    }
    for (const draft of variantDrafts) {
      if (!isValidInternalCode(draft.internal_code)) {
        toast.warning(
          `O código interno da variação "${draft.variant_name}" pode ter no máximo ${INTERNAL_CODE_MAX_LENGTH} caracteres.`,
        );
        return;
      }
    }

    const isService = activeTab === 'service';
    const fiscalCols = {
      lc116_item: isService ? (lc116Item.trim() || null) : null,
      iss_aliquota: isService && issAliquota ? Number(issAliquota.replace(',', '.')) : null,
      // Produto nao tem ISS: `false` explicito, nao `null` (a coluna e NOT NULL).
      iss_retido: isService && issRetido,
      nbs: isService ? (nbs.trim() || null) : null,
      cclasstrib: isService ? (cclasstrib.trim() || null) : null,
    };

    const isTracked = !isService && trackStock;
    const minStockValue = minStock.trim() ? Number(minStock.replace(',', '.')) : null;

    const stockCols = hasVariants
      ? { track_stock: false, min_stock: null }
      : {
          track_stock: isTracked,
          min_stock: isTracked && minStockValue != null ? minStockValue : null,
        };
    const variantStockCols = {
      track_stock: trackStock,
      min_stock: trackStock && minStockValue != null ? minStockValue : null,
    };
    const stockAutomationCols = {
      stock_deduction_mode: isService ? null : stockDeductionMode,
    };
    const parentBarcode = activeTab === 'product' && !hasVariants ? barcode.trim() || null : null;
    const parentInternalCode = hasVariants ? null : normalizeInternalCode(internalCode);

    setSaving(true);

    const online = await checkOnline();

    if (hasVariants && !online) {
      setSaving(false);
      toast.warning('Gerenciar variações exige conexão com a internet.');
      return;
    }

    const savedBundle = (bundlesByParent.get(String(editingItem?.id ?? '')) ?? []) as BundleItemRow[];
    const bundleChanged = !sameBundle(savedBundle, bundleDraft);
    if (bundleChanged && !online) {
      setSaving(false);
      toast.warning('Gerenciar materiais inclusos exige conexão com a internet.');
      return;
    }

    const variantRow = (draft: VariantDraft, parentId: string, parentCompanyId?: string | null) => ({
      user_id: user!.id,
      company_id: parentCompanyId ?? activeCompany?.id ?? null,
      parent_product_id: parentId,
      variant_name: draft.variant_name.trim(),
      name: name.trim(),
      description: description.trim() || null,
      price: parseVariantPrice(draft.price) ?? 0,
      cost: parseCostInput(draft.cost).value,
      type: 'product' as const,
      unit: unit,
      category: category.trim() || null,
      barcode: draft.barcode.trim() || null,
      internal_code: normalizeInternalCode(draft.internal_code),
      ...fiscalCols,
      ...variantStockCols,
      ...stockAutomationCols,
      image_url: imageUrl,
    });

    if (editingItem) {
      if (!online) {
        setSaving(false);
        toast.warning('Não é possível editar item sem internet. Tente novamente quando estiver online.');
        return;
      }

      const existingChildren = (variantsByParent.get(String(editingItem.id)) ?? []) as CatalogItem[];
      const isConversion = existingChildren.length === 0 && hasVariants;

      let carriedBalance = 0;
      if (isConversion && editingItem.track_stock === true) {
        const { data: fresh, error: freshError } = await supabase
          .from('products')
          .select('stock_quantity, track_stock')
          .eq('id', editingItem.id)
          .maybeSingle();

        if (freshError) {
          setSaving(false);
          toast.error('Não foi possível ler o saldo atual do estoque. Tente novamente.');
          return;
        }

        const balance = fresh?.track_stock ? Number(fresh.stock_quantity ?? 0) : 0;
        if (balance !== 0) {
          const { error: zeroError } = await supabase.rpc('apply_stock_movement', {
            p_product_id: editingItem.id,
            p_delta: -balance,
            p_reason: 'adjustment',
            p_quote_id: null,
            p_service_order_id: null,
            p_note: trackStock
              ? 'Saldo transferido para as variações do produto'
              : 'Saldo zerado: o produto passou a ser vendido por variações',
          });
          if (zeroError) {
            setSaving(false);
            toast.error(`Não foi possível transferir o saldo do estoque: ${zeroError.message}`);
            return;
          }
          carriedBalance = balance;
        }
      }

      const { error } = await supabase
        .from('products')
        .update({
          name: name.trim(),
          description: description.trim() || null,
          price: priceValue,
          ...costCols,
          type: activeTab,
          unit: unit,
          category: category.trim() || null,
          barcode: parentBarcode,
          internal_code: parentInternalCode,
          ...fiscalCols,
          ...stockCols,
          ...stockAutomationCols,
          image_url: imageUrl,
        })
        .eq('id', editingItem.id);

      if (error) {
        setSaving(false);
        toast.error('Erro ao atualizar item');
        return;
      }

      const draftIds = new Set(variantDrafts.map((d) => d.id).filter(Boolean) as string[]);
      const removed = existingChildren.filter((c) => !draftIds.has(String(c.id)));

      if (removed.length > 0) {
        const { error: removeError } = await deleteProductsWithVariants(removed.map((c) => String(c.id)));
        if (removeError) {
          setSaving(false);
          toast.error('Erro ao remover variações');
          return;
        }
      }

      let firstVariantId: string | null = null;

      for (const draft of variantDrafts) {
        if (draft.id) {
          const { error: upError } = await supabase
            .from('products')
            .update(variantRow(draft, String(editingItem.id), editingItem.company_id))
            .eq('id', draft.id);
          if (upError) {
            setSaving(false);
            toast.error(`Erro ao salvar a variação "${draft.variant_name}"`);
            return;
          }
          if (!firstVariantId) firstVariantId = String(draft.id);
        } else {
          const { data: inserted, error: insError } = await supabase
            .from('products')
            .insert(variantRow(draft, String(editingItem.id), editingItem.company_id))
            .select('id');
          if (insError) {
            setSaving(false);
            toast.error(`Erro ao criar a variação "${draft.variant_name}"`);
            return;
          }
          if (!firstVariantId && inserted?.[0]) firstVariantId = String(inserted[0].id);
        }
      }

      const photoError = await persistProductPhotos(String(editingItem.id));
      if (photoError) {
        setSaving(false);
        toast.error(photoError);
        return;
      }

      if (carriedBalance !== 0 && firstVariantId && trackStock) {
        const { error: creditError } = await supabase.rpc('apply_stock_movement', {
          p_product_id: firstVariantId,
          p_delta: carriedBalance,
          p_reason: 'adjustment',
          p_quote_id: null,
          p_service_order_id: null,
          p_note: 'Saldo recebido do produto antes das variações',
        });
        if (creditError) {
          setSaving(false);
          toast.error(`O saldo do estoque não pôde ser transferido: ${creditError.message}`);
          return;
        }
      }

      if (bundleChanged) {
        const bundleError = await persistBundleItems(String(editingItem.id));
        if (bundleError) {
          setSaving(false);
          toast.error(bundleError);
          fetchBundles();
          return;
        }
        await fetchBundles();
      }

      setSaving(false);

      if (carriedBalance !== 0) {
        window.dispatchEvent(new CustomEvent('stock-updated'));
      }

      toast.success(activeTab === 'service' ? 'Serviço atualizado!' : 'Produto atualizado!');
    } else {
      if (!online) {
        // A fila carrega as fotos, então elas passam a ter dono: o desmonte da
        // tela não pode mais apagá-las.
        savedRef.current = true;
        saveCatalogItemOffline({
          item: {
            name: name.trim(),
            description: description.trim() || null,
            price: priceValue,
            ...costCols,
            type: activeTab,
            unit: unit,
            category: category.trim() || null,
            barcode: parentBarcode,
            internal_code: parentInternalCode,
            ...fiscalCols,
            ...stockCols,
            ...stockAutomationCols,
            image_url: imageUrl,
            product_photos: productPhotos,
          },
          userId: user!.id,
          companyId: activeCompany?.id ?? null,
        });

        setSaving(false);
        successFeedback();
        window.dispatchEvent(new CustomEvent('catalog-updated'));
        navigate('/catalog');
        return;
      }

      const { data: createdParent, error } = await supabase
        .from('products')
        .insert({
          user_id: user!.id,
          company_id: activeCompany?.id ?? null,
          name: name.trim(),
          description: description.trim() || null,
          price: priceValue,
          ...costCols,
          type: activeTab,
          unit: unit,
          category: category.trim() || null,
          barcode: parentBarcode,
          internal_code: parentInternalCode,
          ...fiscalCols,
          ...stockCols,
          ...stockAutomationCols,
          image_url: imageUrl,
        })
        .select('id');

      if (error) {
        setSaving(false);
        toast.error('Erro ao salvar item');
        return;
      }

      const newParentId = createdParent?.[0] ? String(createdParent[0].id) : null;
      if (newParentId) {
        const photoError = await persistProductPhotos(newParentId);
        if (photoError) {
          setSaving(false);
          toast.error(photoError);
          navigate('/catalog');
          return;
        }
      }
      if (hasVariants && newParentId) {
        for (const draft of variantDrafts) {
          const { error: insError } = await supabase.from('products').insert(variantRow(draft, newParentId));
          if (insError) {
            setSaving(false);
            toast.error(`Erro ao criar a variação "${draft.variant_name}"`);
            navigate('/catalog');
            return;
          }
        }
      }

      if (bundleDraft.length > 0 && newParentId) {
        const bundleError = await persistBundleItems(newParentId);
        if (bundleError) {
          setSaving(false);
          toast.error(bundleError);
          fetchBundles();
          navigate('/catalog');
          return;
        }
        await fetchBundles();
      }

      setSaving(false);
      toast.success(activeTab === 'service' ? 'Serviço salvo!' : 'Produto salvo!');
    }

    window.dispatchEvent(new CustomEvent('catalog-updated'));
    navigate('/catalog');
  };

  const handleDelete = async () => {
    if (!editingItem) return;
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }

    setDeleting(true);

    const online = await checkOnline();
    if (!online && user) {
      if (String(editingItem.id).startsWith('offline_')) {
        removeFromOfflineQueue(editingItem.id);
      } else {
        addToOfflineQueue('delete_catalog_item', { itemId: editingItem.id, userId: user.id });
      }

      const idsToRemove = new Set(withVariantIds([String(editingItem.id)], items));
      const cached = (getCachedCatalog(user.id) || []) as any[];
      const next = cached.filter((p) => !idsToRemove.has(String(p.id)));
      cacheCatalog(user.id, next);
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('catalog-updated'));

      setDeleting(false);
      toast.success('Item excluído localmente! Será sincronizado quando houver internet.', { icon: '📴' });
      setDeleteDialogOpen(false);
      navigate('/catalog');
      return;
    }

    const { error } = await deleteProductsWithVariants([String(editingItem.id)]);

    setDeleting(false);

    if (error) {
      toast.error('Erro ao excluir item');
      return;
    }

    toast.success('Item excluído!');
    setDeleteDialogOpen(false);
    window.dispatchEvent(new CustomEvent('catalog-updated'));
    navigate('/catalog');
  };

  const handleDuplicate = async () => {
    if (!editingItem) return;

    setSaving(true);

    const copyName = `${editingItem.name} (cópia)`;
    const { data: createdCopy, error } = await supabase
      .from('products')
      .insert({
        user_id: user!.id,
        company_id: activeCompany?.id ?? editingItem.company_id ?? null,
        name: copyName,
        description: editingItem.description,
        price: editingItem.price,
        cost: editingItem.cost ?? null,
        type: editingItem.type,
        unit: editingItem.unit,
        category: editingItem.category,
        barcode: null,
        internal_code: null,
        lc116_item: editingItem.lc116_item,
        iss_aliquota: editingItem.iss_aliquota,
        iss_retido: editingItem.iss_retido === true,
        nbs: editingItem.nbs,
        cclasstrib: editingItem.cclasstrib,
        track_stock: editingItem.track_stock,
        min_stock: editingItem.min_stock,
        stock_deduction_mode: editingItem.stock_deduction_mode ?? null,
        image_url: editingItem.image_url,
      })
      .select('id');

    const copyId = createdCopy?.[0] ? String(createdCopy[0].id) : null;
    if (!error && copyId) {
      const copyPhotoError = await persistProductPhotos(copyId);
      if (copyPhotoError) {
        setSaving(false);
        toast.error(copyPhotoError);
        return;
      }
      const children = (variantsByParent.get(String(editingItem.id)) ?? []) as CatalogItem[];
      for (const child of children) {
        const { error: childError } = await supabase.from('products').insert({
          user_id: user!.id,
          company_id: activeCompany?.id ?? child.company_id ?? null,
          parent_product_id: copyId,
          variant_name: child.variant_name,
          name: copyName,
          description: child.description,
          price: child.price,
          cost: child.cost ?? null,
          type: 'product',
          unit: child.unit,
          category: child.category,
          barcode: null,
          internal_code: null,
          track_stock: child.track_stock,
          min_stock: child.min_stock,
          stock_deduction_mode: child.stock_deduction_mode ?? null,
        });
        if (childError) {
          setSaving(false);
          toast.error('Erro ao duplicar as variações');
          navigate('/catalog');
          return;
        }
      }
    }

    setSaving(false);

    if (error) {
      toast.error('Erro ao duplicar item');
      return;
    }

    toast.success('Item duplicado!');
    window.dispatchEvent(new CustomEvent('catalog-updated'));
    navigate('/catalog');
  };

  const currentUnitGroups = unitGroupsIncluding(unit, activeTab);

  if (!user) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Catálogo</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para gerenciar seu catálogo
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* Cadastro segue a régua do formulário de cliente: o NOME é o valor ao   */
  /* vivo (a identidade que está sendo montada), e o dinheiro fica na       */
  /* sublinha. No orçamento é o contrário porque lá o que se monta é preço. */
  const tipoLabel = (editingItem ? editingItem.type : activeTab) === 'service' ? 'Serviço' : 'Produto';
  const heroFallbackName = `Novo ${tipoLabel.toLowerCase()}`;
  const heroSubtitle = variantDrafts.length > 0
    ? 'Preço definido por variação'
    : [
        parseBRLInput(price) > 0
          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              parseBRLInput(price),
            )
          : null,
        unit ? unitLabel(unit) : null,
      ]
        .filter(Boolean)
        .join(' · ') || 'Preencha os dados abaixo';

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {!canWrite ? 'Somente leitura' : editingItem ? 'Editando' : 'Novo cadastro'}
          </p>
          {/* O tipo é imutável nesta tela (vem da rota/estado de navegação), por
              isso é TÍTULO e não seletor: um serviço não vira produto aqui. */}
          <h1 className="truncate text-lg font-bold leading-tight">{tipoLabel}</h1>
        </div>

        {editingItem && canWrite && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
            onClick={handleDuplicate}
            disabled={saving}
            aria-label="Duplicar item"
            title="Duplicar item"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
        {editingItem && canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-red-500/80 hover:text-white"
            onClick={() => setDeleteDialogOpen(true)}
            aria-label="Excluir item"
            title="Excluir item"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Nome em largura cheia e `line-clamp-2` (não `truncate`): é texto livre,
          e "Instalação de ar-condicionado split 12000 BTUs" não cabe numa linha
          em 360px — cortar logo o que identifica o item é a armadilha #52. */}
      <div className="relative mt-5">
        <p className="line-clamp-2 text-2xl font-bold leading-tight">
          {name.trim() || <span className="text-white/60">{heroFallbackName}</span>}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">{heroSubtitle}</p>
      </div>
    </div>
  );

  if (isEdit && loadingItem) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-3">
          {/* IDENTIFICAÇÃO */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="name">Nome *</FieldLabel>
              <Input
                id="name"
                placeholder={activeTab === 'service' ? 'Ex: Instalação de ar-condicionado' : 'Ex: Cabo HDMI 2m'}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="description">Descrição</FieldLabel>
              <Textarea
                id="description"
                placeholder="Detalhes do item..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-xl bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="category">Categoria</FieldLabel>
              <Input
                id="category"
                list="catalog-categories"
                placeholder="Ex: Elétrica, Hidráulica, Materiais..."
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={FIELD_INPUT}
              />
              <datalist id="catalog-categories">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
          </div>

          {/* PREÇO, UNIDADE E CÓDIGOS.
              EMPILHADOS, não lado a lado: medido a 360px, o par Preço|Unidade
              deixa 95px para o valor da unidade e quase todo rótulo real passa
              disso ("Hora trabalhada (h)" = 121px, "Homem-hora (hr/homem)" =
              162px). O `line-clamp-1` do SelectTrigger cortaria justamente a
              palavra que diz a unidade (armadilha #52). Em linha inteira sobram
              252px e o maior rótulo cabe. */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="price">Preço {variantDrafts.length === 0 && '*'}</FieldLabel>
              {variantDrafts.length > 0 ? (
                <p className="flex h-10 items-center text-sm text-muted-foreground">
                  Definido por variação
                </p>
              ) : (
                <div className="relative">
                  <Input
                    id="price"
                    placeholder="0,00"
                    inputMode="numeric"
                    value={price}
                    // Máscara de moeda: só dígitos, entrando pela direita
                    // (1 → 0,01 … 150000 → 1.500,00). Sem separador para
                    // digitar, não há como o ponto ficar ambíguo.
                    onChange={(e) => setPrice(maskBRLInput(e.target.value))}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-10 w-10 text-muted-foreground"
                    onClick={() => setCalculatorOpen(true)}
                    aria-label="Calcular preço por markup ou margem"
                    title="Calcular preço por markup ou margem"
                  >
                    <Calculator className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="unit">Unidade *</FieldLabel>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger id="unit" className={FIELD_INPUT}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-72">
                  {currentUnitGroups.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                        {group.label}
                      </SelectLabel>
                      {group.options.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="cost">Custo</FieldLabel>
              {variantDrafts.length > 0 ? (
                <p className="flex h-10 items-center text-sm text-muted-foreground">
                  Definido por variação
                </p>
              ) : (
                <>
                  {/* MASCARADO como o preço logo acima (#157). Vazio continua
                      vazio: custo ausente NÃO é custo zero (#61). */}
                  <MoneyTextInput
                    id="cost"
                    value={cost}
                    onChange={setCost}
                    className="min-w-0"
                  />
                  <p className="text-xs text-muted-foreground">
                    Quanto este item custa para você. Opcional — usado para calcular margem e o
                    valor investido em estoque.
                  </p>
                </>
              )}
            </div>

            {variantDrafts.length === 0 && (
              <div className="space-y-1.5">
                <FieldLabel htmlFor="internal-code">Código interno</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="internal-code"
                    placeholder="Ex: CAM-P-AZ"
                    value={internalCode}
                    onChange={(e) => setInternalCode(e.target.value)}
                    maxLength={INTERNAL_CODE_MAX_LENGTH}
                    className={cn(FIELD_INPUT, 'font-mono')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={handleGenerateInternalCode}
                    aria-label="Gerar código interno"
                    title="Gerar código a partir do nome"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Sua referência interna. Diferente do código de barras, que vem do fabricante —
                  use o <RefreshCw className="inline h-3 w-3" /> se o item não tem etiqueta de fábrica.
                </p>
              </div>
            )}

            {activeTab === 'product' && variantDrafts.length === 0 && (
              <div className="space-y-1.5">
                <FieldLabel htmlFor="barcode">Código de barras</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="barcode"
                    inputMode="numeric"
                    placeholder="Ex: 7891234567895"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    className={cn(FIELD_INPUT, 'font-mono')}
                  />
                  {showBarcodeButton && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={handleScanBarcodeField}
                      disabled={isBarcodeScanning}
                      aria-label="Escanear código de barras"
                      title="Escanear código de barras"
                    >
                      {isBarcodeScanning ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ScanLine className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* CONTROLE DE ESTOQUE (produto, PRO) */}
          {activeTab === 'product' && inventory.allowed && (
            <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Controlar estoque</p>
                    <p className="text-xs text-muted-foreground">
                      Acompanhe a quantidade em estoque deste produto.
                    </p>
                  </div>
                </div>
                <Switch checked={trackStock} onCheckedChange={setTrackStock} aria-label="Controlar estoque" />
              </div>

              {trackStock && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <FieldLabel htmlFor="min-stock">Estoque mínimo (opcional)</FieldLabel>
                    <Input
                      id="min-stock"
                      inputMode="decimal"
                      placeholder="Ex: 5"
                      value={minStock}
                      onChange={(e) => setMinStock(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel>Baixa deste produto</FieldLabel>
                    <Select
                      value={stockDeductionMode ?? PRODUCT_STOCK_MODE_INHERIT}
                      onValueChange={(value) =>
                        setStockDeductionMode(productStockModeFromSelect(value))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRODUCT_STOCK_MODE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      “Herdar” mantém a automação definida nas configurações de estoque.
                      Isto não desativa o controle de saldo.
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {variantDrafts.length > 0
                      ? 'Cada variação tem o próprio saldo. Ajuste as entradas e saídas na página de Estoque.'
                      : editingItem
                        ? 'Ajuste as entradas e saídas na página de Estoque.'
                        : 'O saldo começa em 0. Registre a entrada inicial na página de Estoque.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* VARIAÇÕES (produto) — o mesmo bloco que o card de item do
              orçamento usa, para não existirem duas implementações da regra. */}
          {activeTab === 'product' && (
            <div className={cn(CONTENT_CARD, 'p-4')}>
              <VariantDraftsSection
                value={variantDrafts}
                onChange={setVariantDrafts}
                isOnline={isOnline}
                allowed={productVariants.allowed}
                onRequirePro={() => productVariants.require()}
                generateCode={(source) => generateUniqueCode(source)}
                seed={{ price, cost, barcode, internalCode }}
                parentName={name}
                showStock={trackStock}
              />
            </div>
          )}

          {activeTab === 'service' && (
            <BundleMaterialsSection
              parentId={editingItem ? String(editingItem.id) : ''}
              products={items}
              parentsWithVariants={parentsWithVariants}
              kitParentIds={kitParentIds}
              materialIds={materialIds}
              value={bundleDraft}
              onChange={setBundleDraft}
              isOnline={isOnline}
              companyId={activeCompany?.id ?? null}
            />
          )}

          {/* CLASSIFICAÇÃO FISCAL (serviço) — recolhida: é opcional e só interessa
              a quem emite NFS-e, mas são dois campos + uma busca que empurrariam
              as fotos e o salvar para fora da tela. */}
          {activeTab === 'service' && (
            <Accordion type="single" collapsible>
              <AccordionItem value="fiscal" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
                <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                  <span className="flex items-center gap-2">
                    <ReceiptText className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-left">
                      Classificação fiscal
                      <span className="ml-1 font-normal text-muted-foreground">(NFS-e, opcional)</span>
                      {lc116Item && (
                        <span className="block truncate font-mono text-xs font-normal text-muted-foreground">
                          LC 116: {lc116Item}
                        </span>
                      )}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-4">

                <div className="space-y-1.5">
                  <FieldLabel>Serviço (LC 116)</FieldLabel>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Buscar por descrição ou código"
                      value={servicoQuery}
                      onChange={(e) => setServicoQuery(e.target.value)}
                    />
                  </div>
                  {servicoResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border">
                      {servicoResults.map((s) => (
                        <button
                          key={s.codigo}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            const corr = getCorrelacaoByLC116(s.codigo);
                            setLc116Item(s.codigo);
                            setNbs(corr?.nbs ?? '');
                            setCclasstrib(corr?.cClassTrib ?? '');
                            setServicoQuery('');
                          }}
                        >
                          <span className="font-mono text-xs text-muted-foreground">{s.codigo}</span> — {s.descricao}
                        </button>
                      ))}
                    </div>
                  )}
                  {lc116Item && (
                    <p className="text-xs text-muted-foreground">
                      Item LC116: <span className="font-mono">{lc116Item}</span>
                      <button
                        type="button"
                        className="ml-2 text-destructive underline"
                        onClick={() => { setLc116Item(''); setNbs(''); setCclasstrib(''); }}
                      >
                        limpar
                      </button>
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="iss">Alíquota ISS (%)</FieldLabel>
                  <Input
                    id="iss"
                    inputMode="decimal"
                    placeholder="Ex: 5"
                    value={issAliquota}
                    onChange={(e) => setIssAliquota(e.target.value)}
                    className={FIELD_INPUT}
                  />
                </div>

                {/* ISS RETIDO NA FONTE — fica no cadastro porque e propriedade
                    do SERVICO (depende da natureza dele e do tomador), nao de
                    cada nota. Marcado aqui, o formulario da NFS-e ja nasce
                    correto; no XML ele vira `tpRetISSQN=2`. */}
                <div className="flex items-start justify-between gap-3 rounded-lg border border-border/60 p-3">
                  <div className="min-w-0 space-y-0.5">
                    <FieldLabel htmlFor="iss-retido">ISS retido na fonte</FieldLabel>
                    <p className="text-xs text-muted-foreground">
                      Marque se quem contrata o serviço recolhe o ISS. Vem
                      preenchido ao emitir a nota deste serviço.
                    </p>
                  </div>
                  <Switch
                    id="iss-retido"
                    checked={issRetido}
                    onCheckedChange={setIssRetido}
                  />
                </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* FOTOS */}
          <div className={cn(CONTENT_CARD, 'space-y-2 p-4')}>
            <div className="flex items-center justify-between gap-2">
              <FieldLabel>Fotos</FieldLabel>
              <span className="text-xs text-muted-foreground">
                {productPhotoCountLabel(productPhotos.length)} / {PRODUCT_PHOTO_LIMIT}
              </span>
            </div>
            <div className="flex flex-wrap items-start gap-3">
              {productPhotos.map((photo, index) => (
                <div key={`${photo.image_url}-${index}`} className="w-16 space-y-1">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setProductPhotos((current) => moveProductPhotoToPrimary(current, index))}
                      className="block rounded-lg"
                      aria-label={
                        index === 0 ? 'Foto principal' : `Tornar foto ${index + 1} principal`
                      }
                    >
                      <StorageImage
                        src={photo.image_url}
                        alt={index === 0 ? 'Foto principal do item' : `Foto ${index + 1} do item`}
                        className="h-16 w-16 rounded-lg border object-cover"
                      />
                    </button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -right-2 -top-2 h-6 w-6"
                      onClick={() => handleRemovePhoto(index)}
                      aria-label={`Remover foto ${index + 1}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="truncate text-center text-[10px] text-muted-foreground">
                    {index === 0 ? 'Principal' : 'Tornar principal'}
                  </p>
                </div>
              ))}
              {productPhotos.length < PRODUCT_PHOTO_LIMIT && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCapturePhoto}
                  disabled={isCapturing || isUploadingPhoto}
                  className="h-16 w-16 flex flex-col items-center justify-center gap-1"
                >
                  {isCapturing || isUploadingPhoto ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Camera className="h-5 w-5" />
                      <span className="text-[10px]">Adicionar</span>
                    </>
                  )}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              A primeira é a foto principal usada no catálogo, orçamento e PDF.
              Toque em outra para torná-la principal. Recurso PRO.
            </p>
          </div>
        </div>

        {/*
          Barra FLUTUANTE acima da navegação inferior — a mesma das telas de
          orçamento e cliente. Filha DIRETA do container da página: `sticky` só
          desliza dentro do próprio pai. Excluir e duplicar NÃO vêm para cá; são
          ícones do cabeçalho, como nas outras telas de edição.
        */}
        {canWrite && (
          <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
            <Button3D size="sm" className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : editingItem ? (
                'Atualizar'
              ) : (
                'Salvar'
              )}
            </Button3D>
          </div>
        )}
      </div>

      <MarkupCalculatorSheet
        open={calculatorOpen}
        onOpenChange={setCalculatorOpen}
        cost={cost}
        itemName={name.trim() || undefined}
        onApply={({ price: newPrice, cost: newCost }) => {
          // A calculadora devolve "1500,5"; o campo é mascarado e espera
          // "1.500,50". Sem normalizar, a máscara leria os dígitos "15005" na
          // próxima tecla e o preço viraria R$ 150,05.
          setPrice(numberToMaskedBRL(parseBRLInput(newPrice)));
          setCost(newCost);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir item?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O item "{editingItem?.name}" será removido permanentemente do catálogo.
              {editingItem && (kitUsageByChild.get(String(editingItem.id)) ?? 0) > 0 && (
                <>
                  {' '}
                  Ele também será removido dos materiais inclusos de{' '}
                  {kitUsageByChild.get(String(editingItem.id))}{' '}
                  {kitUsageByChild.get(String(editingItem.id)) === 1 ? 'serviço' : 'serviços'}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
